import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * SettingsToggle — the v7 switch (SCRUM-457) used for boolean settings and the
 * permissions-matrix role-draft toggles.
 *
 * Token-faithful port of the prototype `.settings-toggle` (app-shell 7.html):
 * a 36×20 pill (rounded --r-full, bg --bg-3, 1px --border-1) with a 14×14 thumb
 * inset 2px (bg --text-2). `is-on` → track fills/borders --cyan, thumb turns
 * --bg-4 and slides translateX(16px). Rendered as a real button with
 * role="switch" + aria-checked (the prototype uses aria-pressed; role="switch"
 * pairs with aria-checked, the correct ARIA for an on/off control).
 *
 * Token map: --bg-3→bg-bg-3, --border-1→border-border-1, --r-full→rounded-tk-full,
 * --cyan→brand-primary, --text-2→bg-text-2, --bg-4→bg-bg-4,
 * --duration-fast(120ms)/--ease-out.
 */
export type SettingsToggleProps = Omit<
  React.ComponentPropsWithoutRef<'button'>,
  'onChange' | 'type'
> & {
  checked: boolean;
  onCheckedChange?: (checked: boolean) => void;
};

export const SettingsToggle = React.forwardRef<HTMLButtonElement, SettingsToggleProps>(
  function SettingsToggle({ checked, onCheckedChange, className, onClick, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={(e) => {
          onClick?.(e);
          onCheckedChange?.(!checked);
        }}
        className={cn(
          'border-border-1 bg-bg-3 rounded-tk-full relative h-[20px] w-[36px] shrink-0 cursor-pointer border p-0 transition-[background] duration-[120ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
          checked && 'bg-brand-primary border-brand-primary',
          className,
        )}
        {...rest}
      >
        <span
          aria-hidden="true"
          className={cn(
            // Use the `transform` property (not Tailwind's `translate` utility, which
            // emits the CSS `translate:` property) so it matches the prototype's
            // `transform: translateX(16px)` AND animates via transition-[transform].
            'bg-text-2 rounded-tk-full absolute top-[2px] left-[2px] h-[14px] w-[14px] transition-[transform,background] duration-[120ms] ease-[cubic-bezier(0.16,1,0.3,1)]',
            checked && 'bg-bg-4 [transform:translateX(16px)]',
          )}
        />
      </button>
    );
  },
);
